import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faHome,
  faEnvelope,
  faReceipt
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import logoImage from '../assets/images/logo.webp';

export default function Success() {
  const navigate = useNavigate();
  
  // Check the type of success
  const urlParams = new URLSearchParams(window.location.search);
  const type = urlParams.get('type');
  const isQuote = type === 'quote';
  const isService = type === 'service';
  const isCarrier = type === 'carrier';
  const quoteId = urlParams.get('quote_id');

  // Fire appropriate GA event on page load
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      if (isQuote) {
        window.gtag('event', 'free_quote_complete', {
          event_category: 'lead_generation',
          event_label: 'free_quote_success_page',
          event_callback: () => {
            console.log('free_quote_complete event sent');
          },
        });
      } else if (isService) {
        window.gtag('event', 'service_payment_complete', {
          event_category: 'ecommerce',
          event_label: 'service_payment_success_page',
          quote_id: quoteId,
          event_callback: () => {
            console.log('service_payment_complete event sent');
          },
        });
      } else if (isCarrier) {
        window.gtag('event', 'carrier_registration_complete', {
          event_category: 'ecommerce',
          event_label: 'carrier_registration_success_page',
          event_callback: () => {
            console.log('carrier_registration_complete event sent');
          },
        });
      } else {
        window.gtag('event', 'payment_complete', {
          event_category: 'ecommerce',
          event_label: 'payment_success_page',
          event_callback: () => {
            console.log('payment_complete event sent');
          },
        });
      }
    }
  }, [isQuote, isService, isCarrier, quoteId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center px-4">
      <div className="max-w-2xl mx-auto text-center">
        {/* Logo */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <img src={logoImage} alt="Wincrest Logistics" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800">Wincrest Logistics</h1>
        </motion.div>

        {/* Success Icon */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="relative">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-8xl text-green-500 mb-4 drop-shadow-lg"
            />
            <div className="absolute -top-2 -right-2 bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center">
              ✓
            </div>
          </div>
          <h2 className="text-4xl font-bold text-gray-800 mb-2">
            {isQuote ? 'Quote Request Confirmed!' : isService ? 'Service Payment Successful!' : isCarrier ? 'Carrier Registration Submitted!' : 'Payment Successful!'}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-600 mx-auto rounded-full"></div>
        </motion.div>

        {/* Main Success Message */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-8 mb-6">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">
              Thank You!
            </h3>
            <p className="text-xl text-gray-700 leading-relaxed font-medium">
              {isQuote 
                ? 'Your free quote request has been confirmed. Please check your email and we\'ll contact you within 24 hours.'
                : isService
                ? 'Your service payment has been processed successfully. We are now assigning a carrier for your transport.'
                : isCarrier
                ? 'Your carrier registration has been submitted successfully. You now have access to our carrier portal.'
                : 'Please check your email to confirm receipt and we\'ll be in touch soon.'
              }
            </p>
          </div>
          
          <p className="text-gray-600 text-lg leading-relaxed">
            {isQuote
              ? 'Your free quote request has been received and confirmed. Our team will review your request and contact you with a detailed quote within 24 hours.'
              : isCarrier
              ? 'Your carrier registration has been submitted successfully. Registration is completely free. You can now access the carrier portal to view available loads and manage your profile.'
              : 'Your payment has been processed successfully. We have received your transaction and will begin processing your request immediately.'
            }
          </p>

          {/* Next Steps */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center justify-center">
              <FontAwesomeIcon icon={faReceipt} className="mr-2 text-green-500" />
              What happens next?
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              {isCarrier ? (
                <>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-800">Confirmation Email</p>
                      <p className="text-sm text-gray-600">You'll receive a confirmation email shortly</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-800">Access Portal</p>
                      <p className="text-sm text-gray-600">You can now access the carrier portal</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-800">Receive Jobs</p>
                      <p className="text-sm text-gray-600">You'll receive notifications when jobs are assigned</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-800">Team Contact</p>
                      <p className="text-sm text-gray-600">Our team will contact you to complete setup</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-800">Confirmation Email</p>
                      <p className="text-sm text-gray-600">{isService ? 'You\'ll receive a receipt within 5 minutes' : 'You\'ll receive a confirmation email shortly'}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-800">Processing</p>
                      <p className="text-sm text-gray-600">We'll review your request within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-800">Contact</p>
                      <p className="text-sm text-gray-600">Our team will reach out with next steps</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="font-medium text-gray-800">Service Delivery</p>
                      <p className="text-sm text-gray-600">We'll coordinate your logistics needs</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>

        {/* Primary Action Button */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {isCarrier ? (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => navigate('/carrier-portal')}
                className="group inline-flex items-center justify-center px-12 py-5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold text-lg rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <FontAwesomeIcon icon={faReceipt} className="mr-3 text-xl group-hover:animate-bounce" />
                Access Carrier Portal
                <div className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity text-xl">
                  →
                </div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="group inline-flex items-center justify-center px-8 py-3 bg-white text-gray-700 font-semibold rounded-lg shadow-md border border-gray-200 hover:shadow-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-300"
              >
                <FontAwesomeIcon icon={faHome} className="mr-2 group-hover:animate-bounce" />
                Back to Home
              </button>
            </div>
          ) : isService ? (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => navigate('/my-quotes')}
                className="group inline-flex items-center justify-center px-12 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <FontAwesomeIcon icon={faReceipt} className="mr-3 text-xl group-hover:animate-bounce" />
                Continue to My Quotes
                <div className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity text-xl">
                  →
                </div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="group inline-flex items-center justify-center px-8 py-3 bg-white text-gray-700 font-semibold rounded-lg shadow-md border border-gray-200 hover:shadow-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-300"
              >
                <FontAwesomeIcon icon={faHome} className="mr-2 group-hover:animate-bounce" />
                Back to Home
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/')}
              className="group inline-flex items-center justify-center px-12 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <FontAwesomeIcon icon={faHome} className="mr-3 text-xl group-hover:animate-bounce" />
              Back to Home
              <div className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity text-xl">
                →
              </div>
            </button>
          )}
        </motion.div>

        {/* Secondary Action */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <a
            href="mailto:email-pending"
            className="group inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg shadow-md border border-gray-200 hover:shadow-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-300"
          >
            <FontAwesomeIcon icon={faEnvelope} className="mr-2 group-hover:animate-pulse" />
            Contact Support
            <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
              ✉
            </div>
          </a>
        </motion.div>

        {/* Footer */}
        <motion.div
          className="mt-12 pt-8 border-t border-gray-200"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className="text-gray-500 text-sm">
            {isCarrier 
              ? 'Questions about your registration? Contact us at '
              : isQuote
              ? 'Questions about your quote? Contact us at '
              : 'Questions about your payment? Contact us at '}
            <a
              href="mailto:email-pending"
              className="text-green-600 hover:text-green-800 font-medium transition-colors"
            >
              email-pending
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
